<template>
  <section class="py-[120px] xl:py-[140px]" id="features">
    <div class="container">
      <CommonSectionTitle
        class="!items-start !flex-col md:!flex-row justify-between"
        :title="`${$t('landing.history.title')}: ${$t(strategyValue)}`"
        title-class="!mb-2 xl:!mb-14"
      >
        <div
          class="w-full md:w-auto flex items-stretch flex-col md:flex-row gap-2 mb-6 md:mb-0"
        >
          <FormSelect
            :options="yearOptions"
            v-model="yearValue"
            label-key="label"
            :placeholder="$t('landing.history.choose_year')"
            value-key="value"
          />
          <FormSelect
            :options="strategyOptions"
            v-model="strategyValue"
            label-key="label"
            :placeholder="$t('landing.history.choose_strategy')"
            value-key="value"
          />
          <BaseButton
            size="sm"
            :text="$t('landing.history.backtest')"
            :disabled="!yearValue || !strategyValue"
            @click="handleFilterBackTest"
          />
        </div>
      </CommonSectionTitle>
      <Transition name="fade" mode="out-in">
        <div :key="loading">
          <template v-if="loading">
            <div class="w-full h-[500px] flex-y-center justify-center">
              <div class="spinner">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          </template>
          <template v-else>
            <template v-if="statisticsContent && chartContent && tableContent">
              <div>
                <h2
                  class="text-2xl md:text-[32px] leading-130 text-dark font-bold"
                >
                  {{ $t("landing.history.statistics") }}
                </h2>
                <div
                  class="bg-gray-150 border border-gray-250 p-4 rounded-lg grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4"
                >
                  <SectionHistoryInfo
                    v-for="(info, index) in statisticsContent"
                    :key="index"
                    :title="formatMoneyDecimal(info?.value, 2)"
                    :description="$t(info?.title)"
                    :type="info?.key"
                  />
                </div>
                <div class="mt-10">
                  <h2
                    class="text-2xl md:text-[32px] leading-130 text-dark font-bold"
                  >
                    {{ $t("landing.history.chart") }}
                  </h2>
                  <div class="border border-gray-250 py-4 rounded-lg">
                    <LineChart
                      :data="chartContent"
                      index="month"
                      :categories="['accum_profit_percentage']"
                      :colors="['green']"
                      :show-legend="false"
                      :show-tooltip="false"
                      :y-formatter="
                        (tick, i) => {
                          return typeof tick === 'number'
                            ? `${new Intl.NumberFormat('us')?.format(tick)?.toString()}%`
                            : '';
                        }
                      "
                    />
                  </div>
                </div>
                <div class="mt-10">
                  <Table class="border border-gray-150">
                    <TableHeader class="bg-gray-150">
                      <TableRow>
                        <TableHead
                          class="text-base !text-dark !font-bold whitespace-nowrap rtl:text-right"
                        >
                          {{ $t("landing.history.month") }}
                        </TableHead>
                        <TableHead
                          class="text-base !text-dark !font-bold whitespace-nowrap rtl:text-right"
                        >
                          {{ $t("landing.history.total_profit") }}
                        </TableHead>
                        <TableHead
                          class="text-base !text-dark !font-bold whitespace-nowrap rtl:text-right"
                        >
                          {{ $t("landing.history.accum_profit_usd") }}
                        </TableHead>
                        <TableHead
                          class="text-base !text-dark !font-bold whitespace-nowrap rtl:text-right"
                        >
                          {{ $t("landing.history.profit") }}
                        </TableHead>
                        <TableHead
                          class="text-right text-base !text-dark !font-bold whitespace-nowrap rtl:text-right"
                        >
                          {{ $t("landing.history.accum_profit_percent") }}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow v-for="(data, idx) of tableContent" :key="idx">
                        <TableCell class="font-medium">
                          {{ $t(data?.month) }}
                        </TableCell>
                        <TableCell>
                          ${{ formatMoneyDecimal(data?.total_profit, 2) }}
                        </TableCell>
                        <TableCell>
                          ${{ formatMoneyDecimal(data?.accum_profit_price, 2) }}
                        </TableCell>
                        <TableCell
                          >{{ formatMoneyDecimal(data?.profit, 2) }}%</TableCell
                        >
                        <TableCell class="text-right">
                          {{
                            formatMoneyDecimal(
                              data?.accum_profit_percentage,
                              2,
                            )
                          }}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </template>
            <template v-else>
              <CommonNoData
                image="/landing/empty-blog.webp"
                class="mx-auto"
                :title="$t('landing.history.no_history')"
                subtitle=""
              />
            </template>
          </template>
        </div>
      </Transition>
    </div>
  </section>
</template>

<script setup lang="ts">
import SectionHistoryInfo from "@/modules/Landing/components/Section/HistoryInfo.vue";
import CommonSectionTitle from "@/modules/Landing/components/Common/SectionTitle.vue";
import CommonNoData from "@/modules/Landing/components/Common/NoData.vue";
import { LineChart } from "@/components/ui/chart-line";
import FormSelect from "@/modules/Landing/components/Form/Select/Select.vue";
import BaseButton from "@/modules/Landing/components/Base/Button/Button.vue";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/Landing/components/ui/table";

import { historyData } from "@/data/index";
import { formatMoneyDecimal } from "@/utils";
import { ref } from "vue";
const loading = ref(false);
const yearOptions = [
  {
    label: "2020",
    value: 2020,
  },
  {
    label: "2021",
    value: 2021,
  },
  {
    label: "2022",
    value: 2022,
  },
  {
    label: "2023",
    value: 2023,
  },
  {
    label: "2024",
    value: 2024,
  },
];
const strategyOptions = [
  {
    label: "Smart Flow",
    value: "smart_flow",
  },
  {
    label: "Candle Master",
    value: "candle_master",
  },
  {
    label: "Ewo Strike",
    value: "ewo_strike",
  },
  {
    label: "Quantum X",
    value: "quantum_apr",
  },
];
const yearValue = ref("");
const strategyValue = ref("");

const chartInitialValue = ref([
  {
    month: "Initial Deposit",
    accum_profit_percentage: 0,
  },
]);

const coinDetails = ref([
  {
    value: 0,
    key: "total_orders",
    title: "landing.history.total_orders",
  },
  {
    value: 0,
    key: "daily_average_trades",
    title: "landing.history.daily_average_trades",
  },
  {
    value: 0 + "%",
    key: "absolute_drawdown",
    title: "landing.history.absolute_drawdown",
  },
  {
    value: "$ " + 0,
    key: "min_balance",
    title: "landing.history.min_balance",
  },
  {
    value: "$ " + 0,
    key: "max_balance",
    title: "landing.history.max_balance",
  },
  {
    value: 0,
    key: "sharpe_ratio",
    title: "landing.history.sharpe_ratio",
  },
]);

const tableData = ref([
  {
    month: "landing.history.initial_deposit",
    total_profit: 0,
    accum_profit_price: 1000,
    profit: 0,
    accum_profit_percentage: 0,
  },
]);

const statisticsContent = ref(null);
const chartContent = ref(null);
const tableContent = ref(null);
const initialValue = ref(null);

const handleFilterBackTest = () => {
  if (yearValue.value && strategyValue.value) {
    loading.value = true;
    initialValue.value = historyData?.[yearValue.value]?.[strategyValue.value];

    statisticsContent.value = null;
    chartContent.value = null;
    tableContent.value = null;

    if (initialValue.value) {
      coinDetails.value.map(
        (el) => (el.value = initialValue.value?.info?.[el.key]),
      );
      statisticsContent.value = coinDetails.value;
      chartContent.value = [
        ...chartInitialValue.value,
        ...initialValue.value.data,
      ];
      tableContent.value = [...tableData.value, ...initialValue.value.data];
    }
    setTimeout(() => {
      loading.value = false;
    }, 2000);
  }
};
</script>
<style>
.tick,
.domain {
  color: #e5e7eb !important;
}
.tick text {
  fill: hsl(215.4 16.3% 46.9%) !important;
}
</style>
<style>
.spinner {
  position: absolute;
  width: 9px;
  height: 9px;
}

.spinner div {
  position: absolute;
  width: 50%;
  height: 150%;
  background: #16cc53;
  transform: rotate(calc(var(--rotation) * 1deg))
    translate(0, calc(var(--translation) * 1%));
  animation: spinner-fzua35 1s calc(var(--delay) * 1s) infinite ease;
}

.spinner div:nth-child(1) {
  --delay: 0.1;
  --rotation: 36;
  --translation: 150;
}

.spinner div:nth-child(2) {
  --delay: 0.2;
  --rotation: 72;
  --translation: 150;
}

.spinner div:nth-child(3) {
  --delay: 0.3;
  --rotation: 108;
  --translation: 150;
}

.spinner div:nth-child(4) {
  --delay: 0.4;
  --rotation: 144;
  --translation: 150;
}

.spinner div:nth-child(5) {
  --delay: 0.5;
  --rotation: 180;
  --translation: 150;
}

.spinner div:nth-child(6) {
  --delay: 0.6;
  --rotation: 216;
  --translation: 150;
}

.spinner div:nth-child(7) {
  --delay: 0.7;
  --rotation: 252;
  --translation: 150;
}

.spinner div:nth-child(8) {
  --delay: 0.8;
  --rotation: 288;
  --translation: 150;
}

.spinner div:nth-child(9) {
  --delay: 0.9;
  --rotation: 324;
  --translation: 150;
}

.spinner div:nth-child(10) {
  --delay: 1;
  --rotation: 360;
  --translation: 150;
}

@keyframes spinner-fzua35 {
  0%,
  10%,
  20%,
  30%,
  50%,
  60%,
  70%,
  80%,
  90%,
  100% {
    transform: rotate(calc(var(--rotation) * 1deg))
      translate(0, calc(var(--translation) * 1%));
  }

  50% {
    transform: rotate(calc(var(--rotation) * 1deg))
      translate(0, calc(var(--translation) * 1.5%));
  }
}
</style>
